import './Quote.css'

export function LineItem() {
   return <div />
}

function HeaderItem({ label, class_name, inline_style }) {
   return (
      <th className={class_name} scope='col' colSpan='1' style={inline_style}>
         {label}
      </th>
   )
}

/**
 * Pads a header or row (array of cell descriptors) with empty cells inserted
 * before the last cell (Total) so that both Monthly and One-Time tables end
 * up with the same number of columns. This ensures that the Product, List Price,
 * (Effective PEPM), and Quantity columns are vertically aligned between the two
 * tables regardless of how many discount columns each one actually needs.
 *
 * @param {Array} cells - The header or row cell descriptors to pad.
 * @param {number} targetColumnCount - The total number of columns desired after padding.
 * @param {Object} padColumnStyle - The style to apply to the inserted padding columns.
 * @returns {Array} The padded array of cell descriptors.
 */
function padCellsBeforeTotal(cells, targetColumnCount, padColumnStyle) {
   if (!targetColumnCount || cells.length >= targetColumnCount) return cells
   const padCount = targetColumnCount - cells.length
   const padCells = Array.from({ length: padCount }, () => ({
      class: 'qty',
      value: '',
      style: padColumnStyle,
   }))
   return [
      ...cells.slice(0, cells.length - 1),
      ...padCells,
      cells[cells.length - 1],
   ]
}

function Header({
   sectionHeaders,
   tableHeaderClass,
   targetColumnCount,
   padColumnStyle,
}) {
   //TODO: somehow set min-width for each column header as a function of window size maybe?
   const paddedHeaders = padCellsBeforeTotal(
      sectionHeaders,
      targetColumnCount,
      padColumnStyle,
   )
   const headerItems = paddedHeaders.map((header, index) => (
      <HeaderItem
         label={header.value}
         class_name={header.class}
         inline_style={header.style}
         key={index}
      />
   ))
   return (
      <thead className={tableHeaderClass}>
         <tr className={tableHeaderClass}>{headerItems}</tr>
      </thead>
   )
}

function ProductRow({ row, targetColumnCount, padColumnStyle }) {
   if (row == null) return null
   // Filter out productCode cells - they're for internal use only, not for display
   const visibleCells = row.filter(
      (item) => item && item.class !== 'productCode',
   )
   if (visibleCells.length === 0) return null
   const paddedCells = padCellsBeforeTotal(
      visibleCells,
      targetColumnCount,
      padColumnStyle,
   )
   const data = paddedCells.map((item, index) => (
      <td colSpan='1' style={item.style} className={item.class} key={index}>
         <span>{item.value}</span>
      </td>
   ))
   return <tr>{data}</tr>
}

function ProductRows({ rows, targetColumnCount, padColumnStyle }) {
   if (rows.length < 1 || rows == null) {
      return null
   } else {
      const productItems = rows.map((row, index) => (
         <ProductRow
            row={row}
            key={index}
            targetColumnCount={targetColumnCount}
            padColumnStyle={padColumnStyle}
         />
      ))
      return <tbody>{productItems}</tbody>
   }
}

/**
 * Renders a <colgroup> for a table. When `columnWidths` is provided, renders a <col> for each column
 * with an explicit width, ensuring deterministic and consistent column sizing across tables that use
 * the same widths array. This, combined with `table-layout: fixed`, guarantees that columns such as
 * List Price, (Effective PEPM), and Quantity are aligned vertically between Monthly and One-Time tables,
 * regardless of the number of discount columns present.
 *
 * @param {Object} props
 * @param {number} props.numColumns - The number of columns for the table (if columnWidths is not provided).
 * @param {string[]} [props.columnWidths] - Optional array of CSS width strings for each column.
 * @returns {JSX.Element} The rendered <colgroup> element.
 */
function ColGroup({ numColumns, columnWidths }) {
   if (Array.isArray(columnWidths) && columnWidths.length > 0) {
      return (
         <colgroup>
            {columnWidths.map((width, i) => (
               <col key={i} style={{ width }} />
            ))}
         </colgroup>
      )
   }
   return <colgroup span={numColumns}></colgroup>
}

export default function PricingSection({
   sectionName,
   numColumns,
   sectionHeaders,
   lineItems,
   tableClass,
   tableHeaderClass,
   targetColumnCount,
   padColumnStyle,
   columnWidths,
}) {
   if (lineItems == null || lineItems.length < 1) {
      return (
         <div className='tableWrapper nobreak'>
            <div className='sectionTitle avoid-break-after'>
               <span className='sectionLabel avoid-break-after'>
                  {sectionName}
               </span>
               <hr noshade='true' className='tableLine avoid-break-after' />
            </div>
            <table
               className={tableClass}
               border='0'
               cellPadding='0'
               cellSpacing='0'
            >
               <ColGroup numColumns={numColumns} columnWidths={columnWidths} />
               <Header
                  sectionHeaders={sectionHeaders}
                  tableHeaderClass={tableHeaderClass}
                  targetColumnCount={targetColumnCount}
                  padColumnStyle={padColumnStyle}
               />
            </table>
            <wbr />
         </div>
      )
   } else {
      return (
         <div className='tableWrapper nobreak'>
            <div className='sectionTitle avoid-break-after'>
               <span className='sectionLabel avoid-break-after'>
                  {sectionName}
               </span>
               <hr noshade='true' className='tableLine avoid-break-after' />
            </div>
            <table
               className={tableClass}
               border='0'
               cellPadding='0'
               cellSpacing='0'
            >
               <ColGroup numColumns={numColumns} columnWidths={columnWidths} />
               <Header
                  sectionHeaders={sectionHeaders}
                  targetColumnCount={targetColumnCount}
                  padColumnStyle={padColumnStyle}
               />
               <ProductRows
                  rows={lineItems}
                  targetColumnCount={targetColumnCount}
                  padColumnStyle={padColumnStyle}
               />
            </table>
            <wbr />
         </div>
      )
   }
}
